/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type {
  BatchNumberForm,
  ComponentId,
  ConfigurationWithoutRequest,
  EnergyRecord,
  ESSCostingConfiguration,
  H2ElectrolyzerRecord,
  H2LiquefierRecord,
  MerchantPriceOptions,
  Metrics,
  OperationType,
  Output12x24s,
  PlantSimConfiguration,
  PlantTimeseriesTimeframes,
  PostProcessingAggType,
  PVSimConfiguration,
  RelatedObjectLookUpWithArchived,
  ShapeObjectives,
  SimulateBatchRequest,
  ThermalGeneratorRecord,
  TimeseriesOutput,
  WindEnergyRecord,
} from '@poweralpha/common';

export enum ShapeLocation {
  BTM = 'BTM',
  FTM = 'FTM',
}

export enum EnergyTargets {
  LOAD = 'Load',
  SHAPE = 'Shape',
}

export enum AssetType {
  WIND = 'Wind',
  ESS = 'ESS',
  PV = 'PV',
  H2 = 'H2',
  THERMAL = 'Thermal',
}

export enum ShapeInputType {
  SINGLE = 'Single',
  BLOCKS = 'Blocks',
}

export interface MarketInputs {
  merchant_price_option: MerchantPriceOptions;
  market_prices: RelatedObjectLookUpWithArchived | null;
  multiyear_prices: RelatedObjectLookUpWithArchived | null;
  onoffpeak_prices: RelatedObjectLookUpWithArchived | null;
  data_start_year: number | null;
  excess_market_revenue_scalar?: number | null;
  deficit_market_cost_scalar?: number | null;
}

export interface BuySellMarketInputs {
  buy_prices: MarketInputs | null;
  sell_prices: MarketInputs | null;
}

export interface RegMarketInputs {
  reg_raise_market: MarketInputs | null;
  reg_lower_market: MarketInputs | null;
}

export interface CustomDeficitPenalty extends ComponentId {
  custom_block: string;
  custom_block_label: string;
  deficit_penalty: number;
}

export interface PTCInputs {
  base_rate: string;
  escalation_rate: string;
  base_year: string;
  tenor: string;
}

export interface BasePricesInputs {
  view: string;
  prices: RelatedObjectLookUpWithArchived | null;
  fixed_price_base_rate: string;
  fixed_price_esc: string;
}

interface PtcRecInputs {
  ptc: PTCInputs;
  rec: BasePricesInputs;
}

export interface ShapeBlockInputs {
  deficit_penalty_type: ShapeInputType;
  deficit_penalty: string;
  deficit_penalty_blocks: Array<Array<string>>;
  excess_revenue_type: ShapeInputType;
  excess_revenue: string;
  excess_revenue_blocks: Array<Array<string>>;
}

export interface MetricOption {
  name: string;
  value: Metrics;
}

export type GroupBy = 'month' | 'blocks';

export interface AggregationMetric {
  metric: MetricOption | null;
  reliability: number | null;
  groupBy: Array<GroupBy> | null;
  aggregations: Array<PostProcessingAggType> | null;
}

export interface ThermalFormInputs {
  thermal_generator: ThermalGeneratorRecord | null;
  unit_count: number | null;
  design_mw: string | null;
  turndown_ratio: string | null;
  heat_rate: string | null;
  annual_capacity_factor: string | null;
  annual_energy_max: string | null;
  is_fuel_price_fixed: boolean;
  fixed_fuel_price: string | null;
  fuel_price: RelatedObjectLookUpWithArchived | null;
  fuel_price_start_year: number | null;
}

export interface ESSFormInputs {
  configuration: ConfigurationWithoutRequest | null;
  request: ESSCostingConfiguration['request'] | null;
  nameplate_power: string;
  duration: string;
  throughput_cost: string;
  max_annual_cycles: string;
  init_soc: string;
  ramp_cost: string;
  idle_aux_mw: string | null;
  active_aux_mw_per_mw: string | null;
  charge_from_wind: boolean | null;
  charge_from_pv: boolean | null;
  self_discharge_rate: string | null;
  allow_custom_parameters: boolean;
  usable_capacities_at_poi: Array<string> | null;
  roundtrip_efficiency: Array<string> | null;
}

export interface VariablePOIInputs {
  poi_in_profile: EnergyRecord | null;
  poi_in_data_start_year: number | null;
  poi_in_scale: string;
  poi_out_profile: EnergyRecord | null;
  poi_out_data_start_year: number | null;
  poi_out_scale: string;
}
export interface SimulateForm extends BuySellMarketInputs, RegMarketInputs, PtcRecInputs {
  simple_markets: boolean;
  project: {
    cod: string;
    term: string;
    poi_mw_ac: string;
    operation_type: OperationType;
    grid_charge: boolean;
    includePostProcessingInputs: boolean;
    aux_usage: string;
    sim_horizon: string;
    sim_horizon_hours: string | null;
    granularity: string;
    aggregation_metrics: Array<AggregationMetric>;
    block_definitions: Array<Array<string>>;
    includeMarketSpecificInputs: boolean;
    reg_lower_enablement: string;
    reg_raise_enablement: string;
    regulation_max: string;
    poi_in_max_mw: string | null;
    poi_out_max_mw: string | null;
    variable_poi: VariablePOIInputs;
    pvs_shared_injection_limit: boolean;
  };
  pv: {
    pvsim_configuration: ConfigurationWithoutRequest | null;
    pvsim_request: PVSimConfiguration['request'] | null;
    n_inverters: string;
    ilr: string;
    weather_data_start_date: string | null;
    weather_data_end_date: string | null;
    data_start_year: number | null;
    module_degradation: boolean;
    optimize_ptc: boolean;
    optimize_rec: boolean;
  };
  ess: Array<ESSFormInputs>;
  wind: {
    profile: WindEnergyRecord | null;
    n_turbines: string | null;
    aux_power_losses: string;
    transmission_loss: string;
    data_start_year: number | null;
    optimize_ptc: boolean;
    optimize_rec: boolean;
  };
  h2: {
    electrolyzer: H2ElectrolyzerRecord | null;
    liquefier: H2LiquefierRecord | null;
    electrolyzer_design_tpd: string | null;
    electrolyzer_storage_tonnes: string | null;
    liquefier_design_tpd: string | null;
    max_tpd: string | null;
    min_tpd: string | null;
    revenue: string | null;
  };
  thermal: Array<ThermalFormInputs>;
  shape: ShapeBlockInputs & {
    profile: EnergyRecord | null;
    data_start_year: number | null;
    objective: ShapeObjectives;
    scale: string;
    shape_location: ShapeLocation;
  };
  assets: Array<AssetInputs>;
  essIdx: Array<number | null>;
  thermalIdx: Array<number | null>;
}

export interface AssetInputs {
  type: AssetType;
}

export interface ESSBatchForm {
  ess_power: BatchNumberForm;
  ess_duration: BatchNumberForm;
}

export interface ThermalBatchForm {
  unit_count: BatchNumberForm;
  design_mw: BatchNumberForm;
  annual_capacity_factor: BatchNumberForm;
  annual_energy_max: BatchNumberForm;
}

export interface SimulateBatchParamsForm {
  file: File | null;
  poi_mw: BatchNumberForm;
  pv_n_inverters: BatchNumberForm;
  pv_ilr: BatchNumberForm;
  ess: Array<ESSBatchForm>;
  thermal: Array<ThermalBatchForm>;
  wind_n_turbines: BatchNumberForm;
  shape_scale: BatchNumberForm;
  h2_electrolyzer_tpd: BatchNumberForm;
  h2_electrolyzer_storage: BatchNumberForm;
  h2_liquefier_tpd: BatchNumberForm;
  h2_min_tpd: BatchNumberForm;
  h2_max_tpd: BatchNumberForm;
  h2_revenue: BatchNumberForm;
}

export interface SimulateBatchForm {
  config: ConfigurationWithoutRequest | null;
  request: PlantSimConfiguration['request'] | null;
  view: string;
  batch_params: SimulateBatchParamsForm;
}

export interface SimulateBatchRequestForm extends SimulateBatchRequest {
  file?: File | null;
}

export interface DashboardPlotData<FirstYr12x24 = void, TsOutput = void> {
  data8760s: Partial<TsOutput> | Record<string, Partial<TsOutput>>;
  data12x24s: {
    sum: Partial<FirstYr12x24> | Record<string, Partial<FirstYr12x24>>;
    avg?: Partial<FirstYr12x24> | Record<string, Partial<FirstYr12x24>>;
    std?: Partial<FirstYr12x24> | Record<string, Partial<FirstYr12x24>>;
  };
  dataMonthly: {
    sum: Partial<TsOutput> | Record<string, Partial<TsOutput>>;
    avg: Partial<TsOutput> | Record<string, Partial<TsOutput>>;
  };
  annualDataForTerm: {
    sum: Partial<TsOutput> | Record<string, Partial<TsOutput>>;
    avg: Partial<TsOutput> | Record<string, Partial<TsOutput>>;
  };
}

interface StaticPlantPlotLines {
  bess_charge: Array<number>;
  bess_discharge: Array<number>;
  pv: Array<number>;
  shape: Array<number>;
  shape_deficit: Array<number>;
  shape_excess: Array<number>;
  wind: Array<number>;
  markets_sell: Array<number>;
  markets_buy: Array<number>;
  buy_price: Array<number>;
  sell_price: Array<number>;
  plant_out: Array<number>;
  h2_power_in: Array<number>;
}

export type PlantPlotLines = StaticPlantPlotLines &
  Record<`bess${number}_charge`, Array<number>> &
  Record<`bess${number}_discharge`, Array<number>> &
  Record<`thermal${number}_power_out`, Array<number>>;

export const toDashboardPlotData = <FirstYr12x24, TsOutput>(
  results: Partial<PlantTimeseriesTimeframes> | undefined | null,
  key: keyof TimeseriesOutput | keyof Output12x24s,
): DashboardPlotData<FirstYr12x24, TsOutput> | undefined => {
  if (results?.first_yr && !results.first_yr[key]) return undefined;
  return {
    data8760s: results?.first_yr ? results.first_yr[key] : {},
    data12x24s: {
      sum: results?.sum_12x24 ? results.sum_12x24[key] : {},
      avg: results?.avg_12x24 ? results.avg_12x24[key] : {},
      std: results?.std_12x24 ? results.std_12x24[key] : {},
    },
    dataMonthly: {
      sum: results?.monthly_sum ? results.monthly_sum[key] : {},
      avg: results?.monthly_avg ? results.monthly_avg[key] : {},
    },
    annualDataForTerm: {
      sum: results?.annual_for_term_sum ? results.annual_for_term_sum[key] : {},
      avg: results?.annual_for_term_avg ? results.annual_for_term_avg[key] : {},
    },
  } as DashboardPlotData<FirstYr12x24, TsOutput>;
};

export const getNestedAttrFromTs = <FirstYr12x24, TsOutput>(
  data: DashboardPlotData<FirstYr12x24, TsOutput> | undefined,
  dataType: 'annualDataForTerm' | 'dataMonthly',
  nestedKey: string,
) => {
  if (!data) return undefined;
  const sums = data[dataType].sum as Record<string, TsOutput>;
  const avgs = data[dataType].avg as Record<string, TsOutput>;
  return sums && avgs && nestedKey in sums && nestedKey in avgs
    ? {
        sum: sums[nestedKey],
        avg: avgs[nestedKey],
      }
    : undefined;
};

export const getNestedAttrFrom12x24 = <FirstYr12x24, TsOutput>(
  data: DashboardPlotData<FirstYr12x24, TsOutput> | undefined,
  _: 'data12x24s' | 'annualDataForTerm' | 'dataMonthly',
  nestedKey: string,
) => {
  if (!data) return undefined;
  const sums = data.data12x24s.sum as Record<string, FirstYr12x24>;
  const avgs = data.data12x24s.avg as Record<string, FirstYr12x24>;
  return sums && avgs && nestedKey in sums && nestedKey in avgs
    ? {
        sum: sums[nestedKey],
        avg: avgs[nestedKey],
      }
    : undefined;
};
